import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { styled } from '@mui/material/styles';
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { logout } from "../utils/auth";
import { routes } from "../Routes/RoutesName";
import LogoutModal from "./modals/LogoutModal";

const drawerWidth = 240;

const StyledAppBar = styled(AppBar)({
  zIndex: 1300,
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  marginLeft: drawerWidth,
  width: `calc(100% - ${drawerWidth}px)`,
  '@media (max-width: 768px)': {
    marginLeft: 0,
    width: '100%',
  }
});

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end', // Desktop: logout button on right
  '@media (max-width: 768px)': {
    justifyContent: 'space-between', // Mobile: hamburger left, logout right
    padding: '0 16px', // Add some padding for mobile
  }
});

const Backdrop = styled('div')(({ isOpen }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  opacity: isOpen ? 1 : 0,
  visibility: isOpen ? 'visible' : 'hidden',
  transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
  zIndex: 1100,
  '@media (min-width: 769px)': {
    display: 'none'
  }
}));

const SidebarContainer = styled('div')(({ theme, isOpen }) => ({
  width: drawerWidth,
  height: '100vh',
  position: 'fixed',
  left: 0,
  top: 0,
  overflowX: 'hidden',
  backgroundColor: '#ffffff',
  boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
  zIndex: 1200,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  transition: 'transform 0.3s ease-in-out',
  '@media (max-width: 768px)': {
    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
    width: '280px',
    top: 56,
    height: `110vh`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100vh',
    backgroundColor: '#ffffff',
    zIndex: -1,
  },
}));

const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 16px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
});

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  borderRadius: '0',
  padding: '12px 16px',
  minHeight: '48px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isSelected ? '#e0e7ff' : 'transparent',
  '&:hover': {
    backgroundColor: isSelected ? '#e0e7ff' : 'rgba(0, 0, 0, 0.04)',
    '& .MuiListItemIcon-root': {
      color: '#4f46e5',
    },
    '& .nav-text': {
      color: '#12188A',
      fontWeight: '600',
    }
  },
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
    justifyContent: 'center',
    color: isSelected ? '#4f46e5' : '#64748b',
    transition: 'color 0.2s ease-in-out',
  },
  '& .nav-text': {
    fontSize: '14px',
    fontWeight: isSelected ? '600' : '500',
    color: isSelected ? '#12188A' : '#000000',
    marginLeft: '12px',
    transition: 'all 0.2s ease-in-out',
  },
}));

const LogoutButton = styled('div')({
  display: 'flex',
  width: '116px',
  height: '44px',
  padding: '0 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '52px',
  border: '1px solid #12188A',
  backgroundColor: '#12188A',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#0e146b',
  },
  '& svg': {
    color: 'white',
    width: '16px',
    height: '16px',
  },
  '& .logout-text': {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '20px',
  }
});

const MenuButton = styled('button')({
  display: 'none',
  background: 'none',
  border: 'none',
  padding: '8px',
  cursor: 'pointer',
  '@media (max-width: 768px)': {
    display: 'block'
  }
});

const MainContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  marginLeft: drawerWidth,
  transition: 'margin-left 0.3s ease-in-out',
  '@media (max-width: 768px)': {
    marginLeft: 0
  }
}));

export default function Sidebar({ Loading, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Close sidebar on window resize if in mobile view
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      // Add a small delay for better UX (optional)
      await new Promise(resolve => setTimeout(resolve, 500));
      await logout(navigate);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    if (!isLoggingOut) {
      setShowLogoutModal(false);
    }
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden' }}>
      <CssBaseline />
      <StyledAppBar position="fixed">
        <StyledToolbar>
          <MenuButton onClick={toggleSidebar}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </MenuButton>
          <LogoutButton onClick={handleLogoutClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="logout-text">Logout</span>
          </LogoutButton>
        </StyledToolbar>
      </StyledAppBar>

      {!Loading && (
        <>
          <Backdrop isOpen={isOpen} onClick={closeSidebar} />
          <SidebarContainer isOpen={isOpen}>
            <LogoContainer>
              <img src="/assets/bosses__logo.svg" alt="Best Bosses Logo" style={{ maxWidth: '160px', height: 'auto' }} />
            </LogoContainer>
            <List style={{ padding: '16px 0', flexGrow: 1 }}>
              {routes.map((route, index) => {
                const isSelected = location.pathname === `/${route.name}` ||
                  (route.name === 'dashboard' && location.pathname === '/');
                const isHovered = hoveredItem === index;

                return (
                  <Link
                    key={index}
                    to={`/${route.name}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    onClick={closeSidebar}
                  >
                    <StyledListItem
                      onMouseEnter={() => setHoveredItem(index)}
                      onMouseLeave={() => setHoveredItem(null)}
                      isSelected={isSelected}
                    >
                      <ListItemIcon>
                        <span style={{
                          fontSize: '20px',
                          color: isSelected || isHovered ? '#4f46e5' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {isSelected || isHovered ? (route.hoverIcon || route.icon) : route.icon}
                        </span>
                      </ListItemIcon>
                      <span className="nav-text">
                        {route.title}
                      </span>
                    </StyledListItem>
                  </Link>
                );
              })}
            </List>
          </SidebarContainer>
          <MainContent>
            {children}
          </MainContent>
        </>
      )}

      <LogoutModal
        open={showLogoutModal}
        onClose={handleLogoutCancel}
        onLogout={handleLogoutConfirm}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}